import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  CryptoTransfer,
  bankAccountGetBankAccountOptions,
  transferCryptoGetCounterpartyOptions,
  transferCryptoGetTransferOptions,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { FeatureFlag, useAnyFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { TransferHeader } from '~/lib/transfers';
import { convertTransferAmount } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';

import { InfoForm } from './_components/Layout';
import { Timeline } from './_components/Timeline';
import { buildStablecoinEvents, getStablecoinExplorerName, getStablecoinExplorerTxUrl } from './_utils/stablecoin';

interface Params {
  id: string;
}

export const PageTransfersStablecoin: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();
  const cryptoEnabled = useAnyFeatureFlag(FeatureFlag.EnableCryptoPayins, FeatureFlag.EnableCryptoPayouts);

  const {
    data: transfer,
    isLoading: isTransferLoading,
    isError: isTransferError,
    error: transferError,
  } = useQuery({
    ...transferCryptoGetTransferOptions({ path: { crypto_transfer_id: id } }),
    enabled: Boolean(id),
  });

  // Enriches the timeline with the funding bank account's name; a failure just falls back to a
  // generic label, so don't navigate away from an otherwise-valid transfer on error.
  const { data: bankAccount, isLoading: isBankAccountLoading } = useQuery({
    ...bankAccountGetBankAccountOptions({ path: { bank_account_id: transfer?.bank_account_id ?? '' } }),
    enabled: Boolean(transfer?.bank_account_id),
  });

  useEffect(() => {
    if (isTransferError) {
      addDangerNotification({ content: (transferError as Error).message });
      navigate(ROUTE.TRANSFERS);
    }
  }, [isTransferError, transferError]);

  const formMethods = useForm<CryptoTransfer>();

  const explorerUrl = useMemo(
    () => (transfer ? getStablecoinExplorerTxUrl(transfer.network, transfer.transaction_hash ?? undefined) : undefined),
    [transfer]
  );

  const explorerName = useMemo(() => getStablecoinExplorerName(transfer?.network), [transfer]);

  const { data: counterparty } = useQuery({
    ...transferCryptoGetCounterpartyOptions({
      path: { crypto_counterparty_id: transfer?.crypto_counterparty_id ?? '' },
    }),
    enabled: Boolean(transfer?.crypto_counterparty_id),
  });

  useEffect(() => {
    if (transfer) {
      formMethods.setValue('transaction_hash', transfer.transaction_hash || '-');
      formMethods.setValue('description', transfer.description || '-');
    }
  }, [transfer, formMethods]);

  const isDataLoading = useMemo(() => {
    return isBankAccountLoading || isTransferLoading;
  }, [isBankAccountLoading, isTransferLoading]);

  // Render nothing (rather than redirect) when gated off so enabled users deep-linking here are not
  // bounced during LaunchDarkly's init window; the content appears once a flag streams in.
  if (!cryptoEnabled) {
    return null;
  }

  return (
    <>
      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferHeader
            amount={transfer.amount}
            createdAt={transfer.created_at}
            isAdding={transfer ? convertTransferAmount('crypto', transfer.is_incoming) > 0 : undefined}
            direction={{ type: transfer.is_incoming ? 'incoming' : 'outgoing' }}
            status={transfer.status}
            transferId={transfer.id}
            transferType="crypto"
          />
        )}

        {transfer && <Timeline entries={buildStablecoinEvents({ transfer, counterparty, bankAccount })} />}

        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<CryptoTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'transaction_hash',
                      label: 'Transaction Hash',
                      children: ({ value }) => {
                        const hash = value ? String(value) : '-';
                        return (
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <CopyInput value={hash} isMonospaced />
                            </div>
                            {explorerUrl && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => window.open(explorerUrl, '_blank', 'noopener,noreferrer')}
                              >
                                {explorerName}
                                <Icon.External style={{ width: 16, height: 16 }} />
                              </Button>
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Transfer Details"
              formattedData={transfer as Record<string, unknown> | undefined}
              rawData={transfer as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
